import { useRef, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../../api/client'
import type { Attachment, Delivery, DeliveryLine, DeliveryOutcome } from '../../api/types'
import { useStores } from '../../app/StoreContext'
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorBox,
  Num,
  Spinner,
  formatDate,
  formatDateTime,
  toIsoDate,
} from '../../ui/primitives'
import { useAsync } from './useAsync'
import {
  OUTCOME_OPTIONS,
  deliveryTitle,
  documentLabel,
  documentTone,
  formatBytes,
  originLabel,
  outcomeLabel,
  outcomeTone,
  slotDetail,
  statusLabel,
  statusTone,
} from './labels'

// Scheda della singola consegna: quello che si sa e quello che si può fare.
//
// Le quattro azioni (riprogramma, allega, entrata merce, esito) hanno ciascuna la propria
// conferma e il proprio messaggio d'errore, accanto al pulsante che le ha scatenate: un rifiuto
// del server è quasi sempre una regola di business — "manca la motivazione", "la data è futura" —
// e va letto dove si stava lavorando, non in cima alla pagina.

/** Riga di sola lettura dell'intestazione. */
function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <span>{children}</span>
    </div>
  )
}

/**
 * Pulsante di azione con conferma, attesa ed errore.
 *
 * La conferma è in linea e non un avviso del browser: resta visibile ciò che si sta
 * confermando, e chi si è sbagliato può annullare senza perdere quanto ha già scritto.
 */
function Azione({
  label,
  confirmText,
  run,
  onDone,
  doneText,
  variant = 'primary',
  disabled,
  disabledReason,
}: {
  label: string
  confirmText: string
  run: () => Promise<void>
  onDone: () => void
  doneText: string
  variant?: 'default' | 'primary' | 'danger' | 'ghost'
  disabled?: boolean
  disabledReason?: string
}) {
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()
  const [done, setDone] = useState(false)

  const esegui = () => {
    setBusy(true)
    setError(undefined)
    run()
      .then(() => {
        setAsking(false)
        setDone(true)
        onDone()
      })
      .catch((e: unknown) => setError(e))
      .finally(() => setBusy(false))
  }

  return (
    <>
      {!asking && (
        <Button
          variant={variant}
          disabled={disabled}
          title={disabled ? disabledReason : undefined}
          onClick={() => {
            setDone(false)
            setError(undefined)
            setAsking(true)
          }}
        >
          {label}
        </Button>
      )}

      {asking && (
        <div className="notice notice-warn">
          <strong>{confirmText}</strong>
          <p className="inline">
            {/* Anche la conferma rispetta il vincolo: se nel frattempo il modulo è tornato
                incompleto, il pulsante si spegne invece di far rifiutare l'operazione al server. */}
            <Button variant={variant} disabled={busy || disabled} onClick={esegui}>
              {busy ? 'Attendere…' : 'Conferma'}
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => setAsking(false)}>
              Annulla
            </Button>
          </p>
        </div>
      )}

      {error !== undefined && <ErrorBox error={error} />}
      {done && (
        <div className="notice notice-ok">
          <strong>{doneText}</strong>
        </div>
      )}
    </>
  )
}

export function DeliveryPage() {
  const { id: rawId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { storeName } = useStores()

  const id = rawId ?? ''
  const path = `/deliveries/${encodeURIComponent(id)}`
  const todayIso = toIsoDate(new Date())

  const delivery = useAsync<Delivery>(() => api.get<Delivery>(path), [path])
  const attachments = useAsync<Attachment[]>(() => api.get<Attachment[]>(`${path}/attachments`), [path])

  // Per il CEDI l'elenco righe è vuoto per costruzione: il gestionale espone i rulli, non gli
  // articoli. Evitiamo perfino la chiamata e lo spieghiamo a video.
  const origin = delivery.data?.origin
  const lines = useAsync<DeliveryLine[]>(
    () => (origin === 'supplier' ? api.get<DeliveryLine[]>(`${path}/lines`) : Promise.resolve([])),
    [path, origin],
  )

  // --- stato dei moduli delle azioni ------------------------------------------------------
  const [rescheduleDate, setRescheduleDate] = useState('')
  const [rescheduleReason, setRescheduleReason] = useState('')

  const [receiptDate, setReceiptDate] = useState(todayIso)
  const [receiptNote, setReceiptNote] = useState('')

  const [outcome, setOutcome] = useState<DeliveryOutcome | ''>('')
  const [receivedRolls, setReceivedRolls] = useState('')

  const fileInput = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<unknown>()
  const [uploadDone, setUploadDone] = useState(false)

  const ricarica = () => {
    delivery.reload()
    attachments.reload()
    lines.reload()
  }

  if (delivery.loading) return <Spinner label="Carico la consegna…" />
  if (delivery.error !== undefined) return <ErrorBox error={delivery.error} onRetry={delivery.reload} />
  if (!delivery.data) return <Empty>Consegna non trovata: forse il collegamento è vecchio.</Empty>

  const d = delivery.data
  const isCedi = d.origin === 'cedi'

  // Regole che l'utente deve conoscere prima di sbagliare, non dopo il rifiuto del server.
  const reasonOk = rescheduleReason.trim().length >= 3
  const receiptDateOk = receiptDate !== '' && receiptDate <= todayIso
  const receiptNoteRequired = receiptDate !== todayIso
  const receiptNoteOk = !receiptNoteRequired || receiptNote.trim().length > 0

  const tipoFileAmmesso = (f: File) => f.type === 'application/pdf' || f.type.startsWith('image/')

  const carica = () => {
    if (!file) return
    setUploading(true)
    setUploadError(undefined)
    api
      .upload<Attachment>(`${path}/attachments`, file)
      .then(() => {
        setFile(null)
        if (fileInput.current) fileInput.current.value = ''
        setUploadDone(true)
        ricarica()
      })
      .catch((e: unknown) => setUploadError(e))
      .finally(() => setUploading(false))
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{deliveryTitle(d)}</h1>
          <div className="muted">
            {d.storeCode} — {storeName(d.storeCode)} · {originLabel(d)}
            {d.orderNumber && ` · ordine ${d.orderNumber}`}
          </div>
        </div>
        <div className="page-tools">
          <Badge tone={statusTone(d.status)}>{statusLabel(d.status)}</Badge>
          {d.isOverdue && <Badge tone="danger">In ritardo</Badge>}
          <Button onClick={() => navigate('/planning')}>← Torna al planning</Button>
        </div>
      </div>

      <Card title="Riepilogo">
        <div className="grid grid-4">
          <Info label="Punto vendita">
            {d.storeCode} — {storeName(d.storeCode)}
          </Info>
          <Info label="Fornitore">
            {d.supplierName} <span className="muted">({d.supplierCode})</span>
          </Info>
          <Info label="Ordine">
            {d.orderNumber ?? '—'} <span className="muted">del {formatDate(d.orderDate)}</span>
          </Info>
          <Info label="Fascia oraria attesa">{slotDetail(d.expectedTimeSlot)}</Info>

          <Info label="Data calcolata">{formatDate(d.calculatedDate)}</Info>
          <Info label="Data riprogrammata">
            {d.scheduledDate ? formatDate(d.scheduledDate) : <span className="muted">non riprogrammata</span>}
          </Info>
          <Info label="Data in agenda">{formatDate(d.effectiveDate)}</Info>
          <Info label="Entrata merce">
            {d.goodsReceiptDate ? formatDate(d.goodsReceiptDate) : <span className="muted">non registrata</span>}
          </Info>

          <Info label="Documento di carico">
            <Badge tone={documentTone(d.documentStatus)}>{documentLabel(d.documentStatus)}</Badge>
          </Info>
          <Info label="Numero documento">
            {d.documentNumber ?? '—'}
            {d.documentDate && <span className="muted"> del {formatDate(d.documentDate)}</span>}
          </Info>
          <Info label="Carico contabilizzato">
            {d.loadedOn ? formatDate(d.loadedOn) : <span className="muted">non ancora</span>}
          </Info>
          <Info label="Esito dichiarato">
            {d.outcome ?
              <Badge tone={outcomeTone(d.outcome)}>{outcomeLabel(d.outcome)}</Badge>
            : <span className="muted">non dichiarato</span>}
          </Info>

          {isCedi && (
            <Info label="Rulli">
              previsti <Num value={d.plannedRolls} /> · ricevuti <Num value={d.receivedRolls} />
            </Info>
          )}
          {d.rescheduleReason && <Info label="Motivo della riprogrammazione">{d.rescheduleReason}</Info>}
          {d.goodsReceiptNote && <Info label="Nota entrata merce">{d.goodsReceiptNote}</Info>}
          {d.assignedTo && <Info label="Documento in carico a">{d.assignedTo}</Info>}
        </div>

        {d.rejectionNote && (
          <div className="notice notice-error">
            <strong>Documento respinto dal controllo</strong>
            <p>{d.rejectionNote}</p>
          </div>
        )}
        {d.suspensionNote && (
          <div className="notice notice-warn">
            <strong>Controllo documento sospeso</strong>
            <p>{d.suspensionNote}</p>
          </div>
        )}
      </Card>

      <div className="grid grid-2">
        {/* --- riprogrammazione ------------------------------------------------------- */}
        <Card title="Riprogramma la consegna">
          <p className="muted">
            La data in agenda diventa quella concordata con il fornitore. La motivazione resta
            scritta sulla consegna: è quello che permette, a fine mese, di capire perché una data
            è cambiata.
          </p>

          <label className="field">
            <span className="field-label">Nuova data</span>
            <input
              type="date"
              value={rescheduleDate || d.effectiveDate?.slice(0, 10) || todayIso}
              onChange={(e) => setRescheduleDate(e.target.value)}
            />
          </label>

          <label className="field">
            <span className="field-label">Motivazione (obbligatoria)</span>
            <textarea
              rows={2}
              value={rescheduleReason}
              placeholder="Es. il fornitore ha comunicato lo slittamento al giorno successivo"
              onChange={(e) => setRescheduleReason(e.target.value)}
            />
            <span className="field-hint">
              Almeno tre caratteri: senza motivazione la riprogrammazione non è tracciabile e il
              pulsante resta disabilitato.
            </span>
          </label>

          <Azione
            label="Riprogramma"
            disabled={!reasonOk}
            disabledReason="Scrivi prima la motivazione."
            confirmText={`Sposto la consegna al ${formatDate(rescheduleDate || d.effectiveDate || todayIso)}?`}
            doneText="Consegna riprogrammata."
            run={() =>
              api.post<void>(`${path}/reschedule`, {
                newDate: rescheduleDate || d.effectiveDate?.slice(0, 10) || todayIso,
                reason: rescheduleReason.trim(),
              })
            }
            onDone={() => {
              setRescheduleReason('')
              ricarica()
            }}
          />
        </Card>

        {/* --- entrata merce ---------------------------------------------------------- */}
        <Card title="Entrata merce">
          <p className="muted">
            Registra il giorno in cui la merce è entrata davvero in negozio. La data non può
            essere futura;{' '}
            <strong>se è diversa da oggi la nota diventa obbligatoria</strong>, perché è l'unico
            modo per spiegare a posteriori perché la registrazione è arrivata in ritardo.
          </p>

          <label className="field">
            <span className="field-label">Data di entrata</span>
            <input
              type="date"
              max={todayIso}
              value={receiptDate}
              onChange={(e) => setReceiptDate(e.target.value)}
            />
            {!receiptDateOk && <span className="field-hint">Indica una data non successiva a oggi.</span>}
          </label>

          <label className="field">
            <span className="field-label">Nota {receiptNoteRequired ? '(obbligatoria)' : '(facoltativa)'}</span>
            <textarea
              rows={2}
              value={receiptNote}
              placeholder={
                receiptNoteRequired ?
                  'Perché la registrazione avviene in una data diversa da oggi'
                : 'Eventuali osservazioni'
              }
              onChange={(e) => setReceiptNote(e.target.value)}
            />
            {receiptNoteRequired && !receiptNoteOk && (
              <span className="field-hint">
                La data scelta non è oggi: scrivi la nota prima di registrare.
              </span>
            )}
          </label>

          <Azione
            label="Registra entrata merce"
            disabled={!receiptDateOk || !receiptNoteOk}
            disabledReason={
              !receiptDateOk ? 'La data non può essere futura.' : 'La nota è obbligatoria per una data diversa da oggi.'
            }
            confirmText={`Registro l'entrata merce del ${formatDate(receiptDate)}?`}
            doneText="Entrata merce registrata."
            run={() =>
              api.post<void>(`${path}/goods-receipt`, {
                date: receiptDate,
                note: receiptNote.trim() || undefined,
              })
            }
            onDone={ricarica}
          />
        </Card>

        {/* --- allegati --------------------------------------------------------------- */}
        <Card title="Documento di carico">
          {isCedi ?
            // Per i rifornimenti dal CEDI il documento non è richiesto al punto vendita: lo
            // produce il centro di distribuzione. Offrire il caricamento contraddirebbe quanto
            // l'applicazione dichiara altrove sullo stato del documento.
            <div className="notice notice-info">
              <strong>Non serve allegare nulla</strong>
              <p>
                Il documento dei rifornimenti dal CEDI è prodotto dal centro di distribuzione e non
                passa dal controllo del back-office. Qui si dichiara l'esito della consegna.
              </p>
            </div>
          : <>
              <p className="muted">
                Allega il documento del fornitore in PDF o come fotografia: da qui passa al
                controllo del back-office.
              </p>

              <label className="field">
                <span className="field-label">Nuovo allegato</span>
                <input
                  ref={fileInput}
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={(e) => {
                    setUploadDone(false)
                    setUploadError(undefined)
                    setFile(e.target.files?.[0] ?? null)
                  }}
                />
                <span className="field-hint">Sono ammessi solo PDF e immagini.</span>
              </label>
            </>
          }

          {file && !tipoFileAmmesso(file) && (
            <div className="notice notice-warn">
              <strong>Tipo di file non ammesso</strong>
              <p>«{file.name}» non è un PDF né un'immagine: scegli un altro file.</p>
            </div>
          )}

          {!isCedi && (
            <div className="inline">
              <Button
                variant="primary"
                disabled={!file || !tipoFileAmmesso(file) || uploading}
                onClick={carica}
                title={!file ? 'Scegli prima un file.' : undefined}
              >
                {uploading ? 'Carico…' : 'Allega documento'}
              </Button>
            </div>
          )}

          {uploadError !== undefined && <ErrorBox error={uploadError} />}
          {uploadDone && (
            <div className="notice notice-ok">
              <strong>Documento allegato.</strong>
            </div>
          )}

          <h3 className="field-label">Allegati presenti</h3>
          {attachments.loading && <Spinner label="Cerco gli allegati…" />}
          {attachments.error !== undefined && (
            <ErrorBox error={attachments.error} onRetry={attachments.reload} />
          )}
          {attachments.data && attachments.data.length === 0 && !attachments.loading && (
            <Empty>Nessun documento allegato a questa consegna.</Empty>
          )}
          {attachments.data && attachments.data.length > 0 && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Dimensione</th>
                    <th>Caricato da</th>
                    <th>Quando</th>
                  </tr>
                </thead>
                <tbody>
                  {attachments.data.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <a href={api.url(`${path}/attachments/${a.id}`)} target="_blank" rel="noreferrer">
                          {a.fileName}
                        </a>
                      </td>
                      <td>{formatBytes(a.byteSize)}</td>
                      <td>{a.uploadedBy}</td>
                      <td>{formatDateTime(a.uploadedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* --- esito, solo per il CEDI ------------------------------------------------- */}
        {isCedi && (
          <Card title="Esito della consegna">
            <p className="muted">
              L'esito si dichiara solo per i rifornimenti dal CEDI: è il dato che alimenta la
              puntualità del centro di distribuzione.
            </p>

            <label className="field">
              <span className="field-label">Esito</span>
              <select value={outcome} onChange={(e) => setOutcome(e.target.value as DeliveryOutcome | '')}>
                <option value="">— scegli —</option>
                {OUTCOME_OPTIONS.map((o) => (
                  <option key={o} value={o}>
                    {outcomeLabel(o)}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span className="field-label">Rulli ricevuti</span>
              <input
                type="number"
                min={0}
                max={9999}
                className="qty-input"
                value={receivedRolls}
                onChange={(e) => setReceivedRolls(e.target.value)}
              />
              <span className="field-hint">
                Previsti dal CEDI: {d.plannedRolls ?? '—'}. Lascia vuoto se non li hai contati.
              </span>
            </label>

            <Azione
              label="Dichiara esito"
              disabled={outcome === ''}
              disabledReason="Scegli prima un esito."
              confirmText={`Dichiaro la consegna come «${outcome === '' ? '' : outcomeLabel(outcome)}»?`}
              doneText="Esito registrato."
              run={() =>
                api.post<void>(`${path}/outcome`, {
                  outcome,
                  receivedRolls: receivedRolls === '' ? undefined : Number(receivedRolls),
                })
              }
              onDone={ricarica}
            />
          </Card>
        )}
      </div>

      {/* --- righe articolo ----------------------------------------------------------- */}
      <Card title="Righe dell'ordine">
        {isCedi ?
          <div className="notice notice-info">
            <strong>Per i rifornimenti dal CEDI il dettaglio articoli non esiste.</strong>
            <p>
              Il centro di distribuzione movimenta rulli, non righe d'ordine: qui si controllano i
              rulli previsti ({d.plannedRolls ?? '—'}) e quelli ricevuti ({d.receivedRolls ?? '—'}),
              e si dichiara l'esito della consegna.
            </p>
          </div>
        : <>
            {lines.loading && <Spinner label="Carico le righe dell'ordine…" />}
            {lines.error !== undefined && <ErrorBox error={lines.error} onRetry={lines.reload} />}
            {lines.data && lines.data.length === 0 && !lines.loading && (
              <Empty>
                L'ordine non ha righe nel gestionale: succede quando l'ordine è stato inviato al
                fornitore fuori dalla procedura, oppure quando è stato annullato.
              </Empty>
            )}
            {lines.data && lines.data.length > 0 && (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Articolo</th>
                      <th>Descrizione</th>
                      <th className="num">Ordinato</th>
                      <th className="num">Spedito</th>
                      <th className="num">Ricevuto</th>
                      <th className="num">Confezione</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.data.map((line) => (
                      <tr key={line.itemCode}>
                        <td className="num">{line.itemCode}</td>
                        <td>{line.description}</td>
                        <td className="num">
                          <Num value={line.orderedQuantity} />
                        </td>
                        <td className="num">
                          <Num value={line.shippedQuantity} />
                        </td>
                        <td className="num">
                          <Num value={line.receivedQuantity} />
                        </td>
                        <td className="num">
                          <Num value={line.packSize} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        }
      </Card>
    </>
  )
}
